import React, { useEffect, useRef } from 'react';
import L from 'leaflet';
import 'leaflet/dist/leaflet.css';
import { Batiment } from '../../../shared/types/game';
import { BUILDING_INTERVENTION_SPAWN_RADIUS_KM, BUILDING_INTERVENTION_SPAWN_RADIUS_METERS } from '../../../shared/constants/gameMap';
import {
  INTERVENTION_OPEN_MANAGE_EVENT,
  type InterventionOpenManageEventDetail,
} from '../../../shared/constants/interventionMapEvents';
import usePageTranslation from '../../../shared/hooks/usePageTranslation';
import {
  readStoredMapView,
  OSM_TILE_URL,
  OSM_ATTRIBUTION,
  MAP_MIN_ZOOM,
  MAP_MAX_ZOOM,
  getBuildingLatLng,
  createBuildingDivIcon,
} from '../map/mapShared';
import type { InstanceRow } from './interventions/instanceModel';
import {
  getInterventionDisplayLines,
  interventionCategoryMapKey,
  interventionMarkerVisualForCategoryKey,
  isTerminalInterventionStatus,
} from './interventions/instanceModel';
import {
  INTERVENTION_MAP_ICON_URL,
  escapeHtml,
  DefaultIcon,
  createLiveVehicleDefaultIcon,
  formatMmSs,
} from './worldMapHelpers';

L.Marker.prototype.options.icon = DefaultIcon;

interface ContextMenuPosition {
  lat: number;
  lng: number;
  x: number;
  y: number;
}

export interface LiveVehicleTrack {
  vehicle_id: number;
  intervention_id: number;
  nom?: string | null;
  type_nom?: string | null;
  dispatch_status: string;
  lat: number;
  lng: number;
  target_lat?: number | null;
  target_lng?: number | null;
  progress?: number;
  eta_total_sec?: number | null;
  eta_remaining_sec?: number | null;
}

interface WorldMapProps {
  buildings?: Batiment[];
  /** Cercles « secteur interventions » autour des sites (hors CTA). Désactivé par défaut côté parent. */
  showInterventionSectors?: boolean;
  /** Interventions affichées sur la carte (seules les non terminées sont rendues). */
  interventions?: InstanceRow[];
  /** Traces dynamiques des véhicules engagés. */
  liveVehicles?: LiveVehicleTrack[];
}

const WorldMap: React.FC<WorldMapProps> = ({ 
  buildings = [],
  showInterventionSectors = false,
  interventions = [],
  liveVehicles = [],
}) => {
  const { t } = usePageTranslation('game', 'Map');
  const mapRef = useRef<L.Map | null>(null);
  const mapContainerRef = useRef<HTMLDivElement>(null);
  const markersRef = useRef<L.Marker[]>([]);
  const circlesRef = useRef<L.Circle[]>([]);
  const interventionMarkersRef = useRef<L.Marker[]>([]);
  const liveVehicleMarkersRef = useRef<L.Marker[]>([]);
  const liveVehicleLinksRef = useRef<L.Polyline[]>([]);
  const contextMenuPositionRef = useRef<ContextMenuPosition | null>(null);

  // Initialiser la carte (une seule fois)
  useEffect(() => {
    if (!mapContainerRef.current || mapRef.current) return;

    const { center: initialCenter, zoom: initialZoom } = readStoredMapView();

    const map = L.map(mapContainerRef.current, {
      center: initialCenter,
      zoom: initialZoom,
      minZoom: MAP_MIN_ZOOM,
      maxZoom: MAP_MAX_ZOOM,
    });

    L.tileLayer(OSM_TILE_URL, {
      attribution: OSM_ATTRIBUTION,
      maxZoom: 19,
    }).addTo(map);

    // Sauvegarder la position et le zoom à chaque changement avec debounce
    let saveTimeout: NodeJS.Timeout | null = null;
    const saveMapState = () => {
      if (!map) return;
      
      
      // Annuler la sauvegarde précédente si elle est en attente
      if (saveTimeout) {
        clearTimeout(saveTimeout);
      }
      
      // Sauvegarder après un court délai pour éviter trop de sauvegardes
      saveTimeout = setTimeout(() => {
        try {
          if (!map) return;
          const center = map.getCenter();
          const zoom = map.getZoom();
          localStorage.setItem('mapState', JSON.stringify({
            center: [center.lat, center.lng],
            zoom: zoom
          }));
        } catch (error) {
          console.debug('Erreur lors de la sauvegarde de l\'état de la carte:', error);
        }
      }, 300); // Debounce de 300ms
    };

    // Écouter les événements de déplacement et de zoom
    map.on('moveend', saveMapState);
    map.on('zoomend', saveMapState);

    // Sauvegarder périodiquement (toutes les 5 secondes) pour s'assurer que la position est sauvegardée
    const periodicSave = setInterval(() => {
      if (map) {
        saveMapState();
      }
    }, 5000);

    mapRef.current = map;

    // Cleanup
    return () => {
      if (mapRef.current) {
        // Nettoyer le timeout de sauvegarde et sauvegarder immédiatement
        if (saveTimeout) {
          clearTimeout(saveTimeout);
          saveTimeout = null;
        }
        
        // Nettoyer l'intervalle de sauvegarde périodique
        clearInterval(periodicSave);
        
        // Sauvegarder l'état final avant de détruire la carte
        try {
          const center = mapRef.current.getCenter();
          const zoom = mapRef.current.getZoom();
          localStorage.setItem('mapState', JSON.stringify({
            center: [center.lat, center.lng],
            zoom: zoom
          }));
        } catch (error) {
          console.debug('Erreur lors de la sauvegarde finale de l\'état de la carte:', error);
        }

        mapRef.current.off('moveend', saveMapState);
        mapRef.current.off('zoomend', saveMapState);
        mapRef.current.remove();
        mapRef.current = null;
      }
    };
  }, []);

  // Gérer les clics droits sur la carte pour le menu contextuel
  useEffect(() => {
    if (!mapRef.current) return;

    const map = mapRef.current;
    const container = map.getContainer();

    const handleContextMenu = (e: MouseEvent) => {
      // Empêcher le menu contextuel par défaut
      e.preventDefault();
      e.stopPropagation();

      // Convertir les coordonnées de la souris en coordonnées Leaflet
      const x = e.clientX;
      const y = e.clientY;
      
      // Calculer le point de clic dans le système de coordonnées de Leaflet
      const point = map.mouseEventToContainerPoint(e);
      const latlng = map.containerPointToLatLng(point);

      // Stocker la position du menu contextuel
      contextMenuPositionRef.current = {
        lat: latlng.lat,
        lng: latlng.lng,
        x: x,
        y: y
      };

      // Déclencher un événement personnalisé pour notifier le parent
      const event = new CustomEvent('mapcontextmenu', {
        detail: contextMenuPositionRef.current
      });
      window.dispatchEvent(event);
    };

    container.addEventListener('contextmenu', handleContextMenu);

    return () => {
      container.removeEventListener('contextmenu', handleContextMenu);
    };
  }, []);

  // Mettre à jour les marqueurs des bâtiments existants
  useEffect(() => {
    if (!mapRef.current) {
      console.warn('⚠️ La carte n\'est pas encore initialisée');
      return;
    }

    const map = mapRef.current;

    console.log(`🗺️ Mise à jour des marqueurs: ${buildings.length} bâtiment(s) à afficher`);
    
    if (buildings.length === 0) {
      console.warn('⚠️ Aucun bâtiment à afficher');
      return;
    }

    // Supprimer les anciens marqueurs (sauf les temporaires)
    markersRef.current.forEach(marker => {
      if (marker.options.icon?.options.className !== 'temporary-marker') {
        map.removeLayer(marker);
      }
    });
    markersRef.current = markersRef.current.filter(
      m => m.options.icon?.options.className === 'temporary-marker'
    );

    circlesRef.current.forEach((c) => map.removeLayer(c));
    circlesRef.current = [];

    buildings.forEach((building) => {
      console.log('🏢 Traitement du bâtiment:', building.nom, 'Localisation:', building.localisation);
      const { lat, lng, coordsFromLocalisation } = getBuildingLatLng(building);
      const buildingIcon = createBuildingDivIcon(building);

      const marker = L.marker([lat, lng], { icon: buildingIcon })
        .addTo(map)
        .bindPopup(`
          <div style="padding: 8px; min-width: 200px;">
            <h3 style="font-weight: bold; margin-bottom: 4px; color: #1f2937;">
              ${building.nom}
            </h3>
            <p style="color: #6b7280; font-size: 14px; margin: 2px 0;">
              Type: ${building.type}
            </p>
            <p style="color: #6b7280; font-size: 14px; margin: 2px 0;">
              Niveau: ${building.niveau}
            </p>
            ${building.localisation ? `
              <p style="color: #6b7280; font-size: 12px; margin-top: 4px;">
                ${building.localisation}
              </p>
            ` : ''}
          </div>
        `);

      markersRef.current.push(marker);
      console.log(`✅ Marqueur ajouté pour ${building.nom} à [${lat}, ${lng}]`);

      if (
        showInterventionSectors &&
        building.type !== 'cta' &&
        coordsFromLocalisation &&
        building.actif
      ) {
        const circle = L.circle([lat, lng], {
          radius: BUILDING_INTERVENTION_SPAWN_RADIUS_METERS,
          color: '#3b82f6',
          weight: 1,
          fillColor: '#3b82f6',
          fillOpacity: 0.07,
        }).addTo(map);
        circle.bindTooltip(`Secteur interventions — ${BUILDING_INTERVENTION_SPAWN_RADIUS_KM} km`, {
          sticky: true,
        });
        circlesRef.current.push(circle);
      }
    });
    
    console.log(`✅ Total de ${markersRef.current.length} marqueur(s) sur la carte`);
  }, [buildings, showInterventionSectors]);

  // Mettre à jour les marqueurs des interventions en cours
  useEffect(() => {
    if (!mapRef.current) return;
    const map = mapRef.current;

    // Nettoyer les anciens marqueurs
    interventionMarkersRef.current.forEach((m) => map.removeLayer(m));
    interventionMarkersRef.current = [];

    const active = interventions.filter((i) => !isTerminalInterventionStatus(i.statut || ''));
    active.forEach((inv) => {
      const lat = inv.latitude;
      const lng = inv.longitude;
      if (lat == null || lng == null) return;
      if (!Number.isFinite(Number(lat)) || !Number.isFinite(Number(lng))) return;

      const { headline, subline } = getInterventionDisplayLines(inv, 'Intervention');
      const status = String(inv.statut || '').trim() || '—';
      const safeHeadline = escapeHtml(headline);
      const safeSubline = escapeHtml(subline);
      const safeStatus = escapeHtml(status);
      const safeNumero = inv.numero_intervention != null ? escapeHtml(String(inv.numero_intervention)) : '';
      const catKey = interventionCategoryMapKey(inv);
      const { hueRotate, ringColor } = interventionMarkerVisualForCategoryKey(catKey);
      const safeCatNom = escapeHtml(String(inv.mission_category_nom ?? '').trim());

      const invIcon = L.divIcon({
        className: 'intervention-marker',
        html: `
          <div style="
            width: 40px;
            height: 40px;
            border-radius: 999px;
            border: 3px solid ${ringColor};
            background: rgba(17,24,39,0.92);
            box-shadow: 0 2px 10px rgba(0,0,0,0.55);
            display: flex;
            align-items: center;
            justify-content: center;
            overflow: hidden;
          ">
            <img
              src="${INTERVENTION_MAP_ICON_URL}"
              alt=""
              width="32"
              height="32"
              style="width:32px;height:32px;object-fit:contain;display:block;filter:hue-rotate(${hueRotate}deg) saturate(1.12) brightness(1.03);"
            />
          </div>
        `,
        iconSize: [40, 40],
        iconAnchor: [20, 20],
      });

      const marker = L.marker([Number(lat), Number(lng)], { icon: invIcon })
        .addTo(map)
        .bindPopup(`
          <div style="padding: 8px; min-width: 220px;">
            <div style="display:flex; align-items:flex-start; gap:8px; margin-bottom:6px;">
              <img src="${INTERVENTION_MAP_ICON_URL}" width="28" height="28" alt="" style="width:28px;height:28px;object-fit:contain;flex-shrink:0;filter:hue-rotate(${hueRotate}deg) saturate(1.12);" />
              <div>
                <div style="font-weight:700; color:#111827;">${safeHeadline}</div>
                ${
                  safeCatNom
                    ? `<div style="color:#2563eb; font-size:11px; margin-top:2px;">${safeCatNom}</div>`
                    : ''
                }
              </div>
            </div>
            ${subline ? `<div style="color:#6b7280; font-size:12px; margin-bottom:6px;">${safeSubline}</div>` : ''}
            <div style="color:#374151; font-size:12px;">Statut : <strong>${safeStatus}</strong></div>
            ${inv.numero_intervention ? `<div style="color:#6b7280; font-size:11px; margin-top:4px;">#${safeNumero}</div>` : ''}
            <button type="button" class="map-intervention-manage-btn" data-instance-id="${Number(inv.id)}" style="margin-top:10px;width:100%;border-radius:8px;border:1px solid #047857;background:linear-gradient(180deg,#10b981 0%,#059669 100%);color:#fff;font-size:12px;font-weight:700;padding:9px 10px;cursor:pointer;box-shadow:0 1px 2px rgba(0,0,0,0.35),inset 0 1px 0 rgba(255,255,255,0.12);text-shadow:0 1px 0 rgba(0,0,0,0.2);">
              ${escapeHtml(t('interventionSidebar.manage'))}
            </button>
          </div>
        `);

      marker.on('popupopen', () => {
        const root = marker.getPopup()?.getElement();
        if (!root) return;
        const btn = root.querySelector<HTMLButtonElement>('button.map-intervention-manage-btn');
        if (!btn) return;
        const onClick = (ev: MouseEvent) => {
          ev.preventDefault();
          ev.stopPropagation();
          const id = Number(btn.getAttribute('data-instance-id'));
          if (!Number.isFinite(id)) return;
          window.dispatchEvent(
            new CustomEvent<InterventionOpenManageEventDetail>(INTERVENTION_OPEN_MANAGE_EVENT, {
              detail: { instanceId: id },
            })
          );
          marker.closePopup();
        };
        btn.addEventListener('click', onClick);
        marker.once('popupclose', () => btn.removeEventListener('click', onClick));
      });

      interventionMarkersRef.current.push(marker);
    });
  }, [interventions, t]);

  // Mettre à jour les positions live des véhicules engagés
  useEffect(() => {
    if (!mapRef.current) return;
    const map = mapRef.current;

    liveVehicleMarkersRef.current.forEach((m) => map.removeLayer(m));
    liveVehicleMarkersRef.current = [];
    liveVehicleLinksRef.current.forEach((l) => map.removeLayer(l));
    liveVehicleLinksRef.current = [];

    liveVehicles.forEach((v) => {
      if (!Number.isFinite(Number(v.lat)) || !Number.isFinite(Number(v.lng))) return;
      const lat = Number(v.lat);
      const lng = Number(v.lng);
      const targetLat =
        v.target_lat != null && Number.isFinite(Number(v.target_lat)) ? Number(v.target_lat) : null;
      const targetLng =
        v.target_lng != null && Number.isFinite(Number(v.target_lng)) ? Number(v.target_lng) : null;

      if (targetLat != null && targetLng != null) {
        const link = L.polyline(
          [
            [lat, lng],
            [targetLat, targetLng],
          ],
          {
            color: '#38bdf8',
            weight: 2,
            opacity: 0.55,
            dashArray: '6 6',
          }
        ).addTo(map);
        liveVehicleLinksRef.current.push(link);
      }

      const marker = L.marker([lat, lng], {
        icon: createLiveVehicleDefaultIcon(v.dispatch_status),
        zIndexOffset: 500,
      }).addTo(map);

      const label = escapeHtml(String(v.nom || `Véhicule ${v.vehicle_id}`));
      const type = escapeHtml(String(v.type_nom || 'Type inconnu'));
      const status = escapeHtml(String(v.dispatch_status || '—'));
      const pct = `${Math.round((Number(v.progress || 0) || 0) * 100)}%`;
      const etaRemaining = Number(v.eta_remaining_sec ?? NaN);
      const etaLine =
        Number.isFinite(etaRemaining) && etaRemaining > 0
          ? `<br/>ETA restant : <strong>${formatMmSs(etaRemaining)}</strong>`
          : '';
      marker.bindTooltip(
        `<div style="font-size:11px;"><strong>${label}</strong><br/>${type}<br/>${status} · ${pct}${etaLine}</div>`,
        {
          sticky: true,
          direction: 'top',
          opacity: 0.95,
          permanent: Number.isFinite(etaRemaining) && etaRemaining > 0,
        }
      );
      liveVehicleMarkersRef.current.push(marker);
    });
  }, [liveVehicles]);

  return (
    <div 
      ref={mapContainerRef} 
      className="w-full h-full"
      style={{ zIndex: 1 }}
    />
  );
};

export default WorldMap;
